import { ArdoqId, PermissionGroup, UserWithRoles } from '@ardoq/api-types';
import {
  Method,
  fetchJson,
  crudApi,
  toRequestOptions,
  fetchAndRespondWith,
} from './api';
import { ApiResponse } from './types';
import { isArdoqError } from '@ardoq/common-helpers';
import { toIds } from '@ardoq/core';

enum Operation {
  ADD = 'add',
  REMOVE = 'remove',
}

const manageUsers = (
  groupId: ArdoqId,
  userIds: ArdoqId[],
  operation: Operation,
  queryParams: Record<string, any> = {}
): ApiResponse<ArdoqId[]> => {
  const searchParams = new URLSearchParams(queryParams).toString();
  const request = new Request(
    `/api/group/${groupId}/${operation}${searchParams ? `?${searchParams}` : ''}`,
    toRequestOptions(Method.PUT, { userIds })
  );
  return fetchAndRespondWith(request, userIds);
};

const addUsers = ({
  groupId,
  userIds,
}: {
  groupId: ArdoqId;
  userIds: ArdoqId[];
}): ApiResponse<ArdoqId[]> => manageUsers(groupId, userIds, Operation.ADD);

const removeUsers = ({
  groupId,
  userIds,
  force,
}: {
  groupId: ArdoqId;
  userIds: ArdoqId[];
  force?: boolean;
}): ApiResponse<ArdoqId[]> =>
  manageUsers(
    groupId,
    userIds,
    Operation.REMOVE,
    force ? { force } : undefined
  );

const createGroup = ({
  name,
}: {
  name: string;
}): ApiResponse<PermissionGroup> => {
  const request = new Request(
    '/api/group',
    toRequestOptions(Method.POST, { name })
  );
  return fetchJson<PermissionGroup>(request);
};

const createGroupWithUsers = async ({
  name,
  users,
}: {
  name: string;
  users: UserWithRoles[];
}): ApiResponse<PermissionGroup> => {
  const group = await createGroup({ name });
  if (isArdoqError(group)) {
    return group;
  }
  const addedUsers = await addUsers({
    groupId: group._id,
    userIds: toIds(users),
  });
  if (isArdoqError(addedUsers)) {
    return addedUsers;
  }
  return group;
};

type FetchAllParams = {
  includeSpecialGroups?: boolean;
};

export const fetchAllV2 = ({
  includeSpecialGroups,
}: FetchAllParams): ApiResponse<PermissionGroup[]> => {
  const urlParams = new URLSearchParams();
  if (includeSpecialGroups) {
    urlParams.set('include-special-groups', 'true');
  }

  const request = new Request(
    `/api/group?${urlParams}`,
    toRequestOptions(Method.GET, null)
  );
  return fetchJson<PermissionGroup[]>(request);
};

export const groupApi = {
  ...crudApi<PermissionGroup>('group'),
  fetchAllV2,
  create: createGroup,
  addUsers,
  removeUsers,
  createGroupWithUsers,
};
